import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.metafield-rich_text_field').forEach(field => {
    const accordion = field.closest('details');
    if (accordion) {
      const summary = accordion.querySelector('summary');
      if (summary && summary.textContent.toLowerCase().includes('specification')) {
        formatAsTable(field);
      }
    }
  });

  function formatAsTable(field) {
    const htmlContent = field.innerHTML;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    const table = document.createElement('table');
    table.className = 'specification-table';
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.marginTop = '10px';
    
    let hasTable = false;
    
    let plainText = tempDiv.innerHTML.replace(/<\/p>\s*<p.*?>/gi, '<br>').replace(/<p.*?>|<\/p>/gi, '').trim();
    const lines = plainText.split(/<br\s*\/?>/i);
    
    let lastValueTd = null;

    lines.forEach(line => {
      line = line.trim();
      if (!line) return;
      
      const colonIndex = line.indexOf(':');
      if (colonIndex !== -1) {
        const tr = document.createElement('tr');
        tr.style.borderBottom = '1px solid rgba(0,0,0,0.08)';

        const key = line.substring(0, colonIndex).trim();
        const value = line.substring(colonIndex + 1).trim();
        
        tr.innerHTML = `
          <td style="padding: 12px 10px 12px 0; width: 35%; font-weight: 600; vertical-align: top; color: var(--color-foreground); border-right: 1px solid rgba(0,0,0,0.08);">${key}</td>
          <td style="padding: 12px 0 12px 10px; vertical-align: top; color: var(--color-foreground);">${value}</td>
        `;
        table.appendChild(tr);
        lastValueTd = tr.lastElementChild;
        hasTable = true;
      } else {
        if (lastValueTd) {
          lastValueTd.innerHTML += '<br>' + line;
        } else {
          const tr = document.createElement('tr');
          tr.style.borderBottom = '1px solid rgba(0,0,0,0.08)';
          tr.innerHTML = `
            <td colspan="2" style="padding: 12px 0; vertical-align: top; color: var(--color-foreground);">${line}</td>
          `;
          table.appendChild(tr);
        }
      }
    });
    
    if (hasTable) {
      field.innerHTML = '';
      field.appendChild(table);
    }
  }
});
